import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subscription, combineLatest } from 'rxjs';

import { UserClaims } from 'app/@types/dto/user-claims.model';
import { UserTaskNames } from 'app/@types/dto/user-task-names.model';
import { UserState, UserStatus } from '@state/user.state';
import { QuestionnaireState } from '@state/questionnaire.state';

/**
 * The AppRoutingService handles application-level routing at key moments
 */
@Injectable({
    providedIn: 'root',
})
export class AppRoutingService {
    private ROUTE_LOGIN = '/auth/login';
    private ROUTE_MEASURE = '/app/measure';
    private ROUTE_MEASURE_SUMMARY = '/app/measure-summary';
    private ROUTE_ADVANCED_MEASURE = '/app/advanced_measure';
    private ROUTE_RESULTS = '/app/results';
    private ROUTE_RESULTS_DIMENSIONS = '/app/results';
    private ROUTE_SOLUTIONS = '/app/solutions';
    private ROUTE_INTRODUCTION = '/app/solutions/introduction';
    private ROUTE_CONFIGURATION = '/app/solutions/configuration';
    private ROUTE_ACTIONPLAN = '/app/actionplan';
    private ROUTE_CSRD = '/app/csrd';
    private ROUTE_SETTINGS = '/app/settings';
    private ROUTE_ADMIN_HOME = '/admin/home';
    private ROUTE_DEDICATED_ADMIN = '/dedicated_admin/home';
    private ROUTE_ERROR_NOTLOGGED = '/pages/miscellaneous/error/0';
    private ROUTE_ADMIN_ORG_FORM = '/dedicated_admin/home/admin_form';
    private ROUTE_ADMIN_DEDICATEDADMIN = '/admin/home/dedicated_admin_detail';
    private ROUTE_ADMIN_DEDICATEDADMIN_ORGFORM = '/admin/home/dedicated_admin_detail/admin_form';
    private ROUTE_ADMIN_NEW_DEDICATED_ADMIN = '/admin/home/admin_form';

    public wantedRoute: string | undefined;

    constructor(
        private router: Router,
        private store: Store,
    ) {}

    /**
     * Routing to be done after successful signIn
     */
    public async onSignedIn(returnUrl?: string): Promise<boolean> {
        // Refresh userClaims with no specific organization/organizationEntity
        const currentUserObs = this.store.select(UserState.getCurrentUserClaims);
        const currUserStatus = this.store.select(UserState.getUserStatus);
        let sub: Subscription;

        return new Promise<boolean>((resolve) => {
            sub = combineLatest([currentUserObs, currUserStatus]).subscribe(([claims, userStatus]) => {
                if (claims) {
                    const responseStatus = this.store.selectSnapshot(UserState.getResponseStatus);
                    switch (responseStatus) {
                        //I feel like we don't need that anymore
                        // case HttpStatusCode.NoContent:
                        //     // Correct sign-in, but needs onboarding
                        //     this.router.navigate([this.ROUTE_ONBOARDING]);
                        //     break;
                        case HttpStatusCode.Ok:
                            if (claims !== null) {
                                if (userStatus === UserStatus.NONE) {
                                    this.navigateToLoginPage();
                                    break;
                                }
                                //Proceed to landing page (consume wantedRoute one-shot: it captures the
                                //route wanted by the *previous* navigation and must not leak into a
                                //later sign-in, possibly by another account)
                                const wantedRoute = this.wantedRoute;
                                this.wantedRoute = undefined;
                                this.navigateToLandingPage(claims, wantedRoute ?? returnUrl);
                            } else {
                                // Unexpected
                                console.error('Unexpected Userclaims null');
                                this.router.navigate([this.ROUTE_ERROR_NOTLOGGED]);
                            }
                            break;
                        default:
                            // Unexpected
                            console.error(`Unexpected responseStatus ${responseStatus}`);
                            this.router.navigate([this.ROUTE_ERROR_NOTLOGGED, { statusCode: responseStatus }]);
                            break;
                    }
                    if (sub) {
                        sub.unsubscribe();
                    }
                    resolve(true);
                }
            });
        });
    }

    /**
     * Routing to be done after successful onboarding (free user)
     */
    public async onOnboarded(): Promise<boolean> {
        // Refresh userClaims with no specific organization/organizationEntity
        const currentUserObs = this.store.select(UserState.getCurrentUserClaims);
        let sub: Subscription;

        return new Promise<boolean>((resolve) => {
            sub = currentUserObs.subscribe((claims) => {
                if (claims) {
                    const responseStatus = this.store.selectSnapshot(UserState.getResponseStatus);

                    switch (responseStatus) {
                        case HttpStatusCode.NoContent:
                            // After onbaoarding, this is unexpected
                            console.error('Unexpected Userclaims NoContent after onboarding');
                            break;
                        case HttpStatusCode.Ok:
                            if (claims !== null) {
                                // Correct signIn + onboarding, proceed to landing page
                                this.navigateToMeasurePage();
                            } else {
                                // Unexpected
                                console.error('Unexpected Userclaims null after onboarding');
                            }
                            break;
                        default:
                            // Unexpected
                            console.error(`Unexpected responseStatus ${responseStatus} after onboarding`);
                            break;
                    }
                    if (sub) {
                        sub.unsubscribe();
                    }
                    resolve(true);
                }
            });
        });
    }

    /**
     * Navigate to landing page
     */
    public navigateToLandingPage(userClaims: UserClaims, returnUrl?: string) {
        if (userClaims.tasks.some((x) => x.name === 'admin') && !userClaims.is_sudo) {
            this.router.navigate([this.ROUTE_ADMIN_HOME]);
        } else if (userClaims.tasks.some((x) => x.name === UserTaskNames.organization) && !userClaims.is_sudo) {
            this.router.navigate([this.ROUTE_DEDICATED_ADMIN]);
        } else if (userClaims.organization) {
            if (!userClaims.organization.isOnboarded) {
                // Organization has been deactivated
                this.router.navigate([this.ROUTE_MEASURE]);
            } else if (returnUrl) {
                // Go to returnUrl if defined. If a guard rejects it (e.g. a stale returnUrl
                // pointing at a page the signed-in account cannot access), fall back to the
                // default landing page instead of stranding the user on access-denied.
                void this.router.navigateByUrl(returnUrl).then((succeeded) => {
                    if (!succeeded) {
                        this.router.navigate([this.ROUTE_MEASURE_SUMMARY]);
                    }
                });
            } else {
                // Default landing: matrix summary unless an active scan is already loaded
                // (e.g. session restored from prior tab) — in that case, return to the scan.
                const activeQuestionnaire = this.store.selectSnapshot(QuestionnaireState.getQuestionnaire);
                if (activeQuestionnaire) {
                    this.router.navigate([
                        activeQuestionnaire.typeId === 2 ? this.ROUTE_ADVANCED_MEASURE : this.ROUTE_MEASURE,
                    ]);
                } else {
                    this.router.navigate([this.ROUTE_MEASURE_SUMMARY]);
                }
            }
        } else if (!userClaims.organization || userClaims.can_sudo === true) {
            this.router.navigate(['/app/change-organization']);
        }
    }

    /**
     * Navigate to login page
     */
    public navigateToLoginPage() {
        this.router.navigate([this.ROUTE_LOGIN]);
    }

    /**
     * Navigate to measure page
     */
    public navigateToMeasurePage() {
        this.router.navigate([this.ROUTE_MEASURE]);
    }

    /**
     * Navigate to measure page
     */
    public navigateToAdvancedMeasurePage() {
        this.router.navigate([this.ROUTE_ADVANCED_MEASURE]);
    }

    /**
     * Navigate to results page
     * go directly on the dimension sections
     */
    public navigateToResultsPage() {
        this.router.navigate([this.ROUTE_RESULTS_DIMENSIONS]);
    }

    /**
     * Navigate to solutions page
     */
    public navigateToSolutionsPage() {
        this.router.navigate([this.ROUTE_SOLUTIONS]);
    }

    /**
     * Navigate to action plan page
     */
    public navigateToActionPlanPage() {
        this.router.navigate([this.ROUTE_ACTIONPLAN]);
    }

    /**
     * Navigate to solutions page
     */
    public navigateToSolutionIntroductionPage() {
        this.router.navigate([this.ROUTE_INTRODUCTION]);
    }

    /**
     * Navigate to solutions configurations pages
     */
    public navigateToSolutionConfigurationPage(params?: string) {
        const navigationExtras: NavigationExtras = {};

        if (params) {
            navigationExtras.queryParams = { selectedSolution: params };
        }

        this.router.navigate([this.ROUTE_CONFIGURATION], navigationExtras);
    }

    /**
     * Navigate to CSRD page
     */
    public navigateToCSRDPage() {
        this.router.navigate([this.ROUTE_CSRD]);
    }

    /**
     * Navigate to Settings page
     */
    public navigateToSettingsPage() {
        this.router.navigate([this.ROUTE_SETTINGS]);
    }

    /**
     * Navigate to formSubscreen
     */
    public navigateToOganisationForm() {
        this.router.navigate([this.ROUTE_ADMIN_ORG_FORM]);
    }

    public navigateToDedicatedAdminList(dedicatedAdminId: string) {
        const navigationExtras: NavigationExtras = {};

        navigationExtras.queryParams = { dedicatedAdminId: dedicatedAdminId };

        this.router.navigate([this.ROUTE_ADMIN_DEDICATEDADMIN], navigationExtras);
    }

    public navigateToOganisationAdminForm(organizationId?: string) {
        const navigationExtras: NavigationExtras = {};

        if (organizationId) {
            navigationExtras.queryParams = { organizationId: organizationId };
        }
        this.router.navigate([this.ROUTE_ADMIN_DEDICATEDADMIN_ORGFORM], navigationExtras);
    }

    navigateToNewDedicatedUser() {
        this.router.navigate([this.ROUTE_ADMIN_NEW_DEDICATED_ADMIN]);
    }
}
